import React from 'react';
import styled from 'styled-components';
import { Layout } from '../../components/page-layout';
import HeroImg from '../../assets/blog/choose-best-camera.png';
import DogGirlImg from '../../assets/blog/girl-feed-dog.png';
import RelatedBolg from '../../components/RelatedBolg';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 64px 24px 48px 24px;
  font-family: 'Nunito', sans-serif;
`;

const Title = styled.h1`
  font-size: 2.8rem;
  font-weight: 800;
  color: #181818;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Byline = styled.p`
  text-align: center;
  color: #888;
  font-size: 1rem;
  margin-bottom: 2rem;
`;

const HeroImage = styled.img`
  width: 100%;
  max-width: 700px;
  display: block;
  margin: 0 auto 2rem auto;
  border-radius: 24px;
`;

const Paragraph = styled.p`
  color: #222;
  font-size: 1.15rem;
  margin-bottom: 1.2rem;
`;

const SectionHeading = styled.h2`
  color: #F87B15;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 2.5rem 0 1rem 0;
`;

const BulletList = styled.ul`
  margin-left: 1.5rem;
  margin-bottom: 1rem;
`;

const Bullet = styled.li`
  font-size: 1.05rem;
  margin-bottom: 0.5rem;
  position: relative;
  padding-left: 1.2em;
  list-style: none;
  span {
    color: #F87B15;
    font-weight: 600;
  }
  &::before {
    content: '';
    display: inline-block;
    position: absolute;
    left: 0;
    top: 0.7em;
    width: 0.32em;
    height: 0.32em;
    background: #222;
    border-radius: 50%;
    margin-right: 0.7em;
    transform: translateY(-50%);
  }
`;

const ProTip = styled.p`
  font-size: 0.98rem;
  font-style: italic;
  color: #888;
  margin-bottom: 2rem;
`;

const FeatureRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  align-items: flex-start;
  margin-bottom: 2.5rem;
  @media (min-width: 900px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const DogImage = styled.img`
  width: 100%;
  max-width: 420px;
  border-radius: 24px;
  margin: 0 auto;
  @media (min-width: 900px) {
    margin: 0 0 0 2rem;
    width: 420px;
    max-width: 420px;
  }
`;

const BottomLine = styled.h3`
  font-style: italic;
  color: #F87B15;
  font-size: 1.25rem;
  margin: 2.5rem 0 1rem 0;
`;

export default function HowToChoose() {
  return (
    <Layout showCommonFooter>
      <Container>
        <Title>How to Choose the Best Home Camera for Your Needs</Title>
        <Byline>By Sensforge Team</Byline>
        <HeroImage src={HeroImg} alt="Family with home camera" />
        <Paragraph>
          Home security cameras have come a long way. From basic motion detection to smart tracking, cloud storage, and AI-powered alerts—it can be overwhelming to choose the right one. But here's the good news: selecting the best camera really comes down to understanding your space and your priorities.
        </Paragraph>
        <Paragraph>
          Whether you're keeping an eye on your front door, watching over a pet, or just wanting peace of mind while you're away, here are the key things to consider when choosing a home camera that fits your lifestyle.
        </Paragraph>
        <SectionHeading>1. Wired or Wireless? Think About Power First</SectionHeading>
        <Paragraph>The first big decision is whether to go with a wired or wireless camera.</Paragraph>
        <BulletList>
          <Bullet>
            <span>Wired Cameras</span> are great if you have a convenient power source nearby. They're typically more reliable over time, with no worries about recharging batteries. Plus, they often support higher video quality.
          </Bullet>
          <Bullet>
            Wireless cameras, on the other hand, give you flexibility. No drilling or complex installation—just place it where you need and connect to Wi-Fi. Perfect for renters or areas where outlets are limited. Just keep in mind: you'll need to stay on top of battery life.
          </Bullet>
        </BulletList>
        <ProTip>
          <b>Pro tip:</b> If you're not sure, start with a <i>wireless model</i>. It's easier to experiment with placement before committing.
        </ProTip>
        <SectionHeading>2. What Area Are You Covering?</SectionHeading>
        <Paragraph>Every space has its own needs.</Paragraph>
        <BulletList>
          <Bullet>
            For small rooms, entryways, or narrow hallways, a fixed-angle camera might be more than enough.
          </Bullet>
          <Bullet>
            But if you're looking to cover a large space—like a living room, garage, or backyard—consider a <span>Pan-tilt Camera</span>. These can swivel horizontally and tilt vertically, to give you more complete coverage with just one device.
          </Bullet>
        </BulletList>

        <SectionHeading>3. Indoor vs. Outdoor Cameras</SectionHeading>
        <Paragraph>This one's straightforward:</Paragraph>
        <BulletList>
          <Bullet>
            <span>Indoor Cameras</span> are usually designed with aesthetics and discretion in mind. They're smaller, quieter, and fit nicely into home decor.
          </Bullet>
          <Bullet>
            Outdoor cameras are built tougher—with weatherproofing, better night vision, and wider detection ranges to handle the elements and longer distances.
          </Bullet>
        </BulletList>
        <ProTip>
          <b>Pro Tip:</b> If you want to see outside, it's best to go with an <i>outdoor camera</i> rather than try to use an indoor camera pointed through a window. The reflection will play tricks on you otherwise.
        </ProTip>

        <SectionHeading>4. Do You Need Ultra-Crisp Video?</SectionHeading>
        <Paragraph>
          Most modern cameras offer 1080p Full HD, which works well for general monitoring. But if you want to capture the details—like identifying faces or license plates—look for a camera with a minimum of 2k video quality.
        </Paragraph>
        <ProTip>
          Pro Tip: Higher resolution usually means more bandwidth usage and larger file sizes. Make sure your internet connection and storage plan can handle it.
        </ProTip>

        <SectionHeading>5. Storage: Cloud or Local?</SectionHeading>
        <Paragraph>How do you want to access and store your footage?</Paragraph>
        <BulletList>
          <Bullet>
            Cloud storage lets you view recordings remotely and keeps data safe even if the camera is damaged or stolen. But there may be a monthly subscription fee.
          </Bullet>
          <Bullet>
            Local storage (like an SD card) avoids those fees, and it's a one-time setup. However, you'll need to monitor storage space and offload the files to your computer or external drive when the card gets full.
          </Bullet>
          <Bullet>
            Some <span>Modern Cameras</span> offer both—giving you the flexibility to switch back and forth.
          </Bullet>
        </BulletList>

        <SectionHeading>6. Smart Features That Make a Difference</SectionHeading>
        <FeatureRow>
          <div>
            <Paragraph>The best cameras do more than just record. Look for features like:</Paragraph>
            <BulletList>
              <Bullet>Motion detection with alerts</Bullet>
              <Bullet>Two-way audio (so you can speak through the camera)</Bullet>
              <Bullet>Night vision</Bullet>
              <Bullet>AI-powered detection (categorize clips by people, pets, vehicles, etc.)</Bullet>
              <Bullet>App support with livestream, playback, and customization options</Bullet>
            </BulletList>
          </div>
          <DogImage src={DogGirlImg} alt="Girl feeding dog by pool" />
        </FeatureRow>

        <SectionHeading>7. Privacy and Data Protections</SectionHeading>
        <Paragraph>Home cameras give you peace of mind—but they also raise privacy questions. Choose a brand that lets you:</Paragraph>
        <BulletList>
          <Bullet>Turn cameras on/off remotely</Bullet>
          <Bullet>Include a privacy shutter</Bullet>
          <Bullet>Mask certain areas from detection</Bullet>
          <Bullet>Use encrypted storage and secure cloud accounts</Bullet>
        </BulletList>
        <Paragraph>Your home should feel safer—not more exposed.</Paragraph>

        <BottomLine>The Bottom Line</BottomLine>
        <Paragraph>
          There's no one-size-fits-all home camera. The best choice depends on your space, lifestyle, and what gives you peace of mind. Many users love to pair cameras together to enjoy indoor, outdoor, and on-the-go video!<br />
          Start with your needs, and you'll find a camera that truly works for you.
        </Paragraph>
        <RelatedBolg currentSlug="how-to-choose-the-best-home-cameras-for-your-needs" />
      </Container>
    </Layout>
  );
}
